import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Loader2, Save, Bell, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/hooks/use-notifications';

interface AdminPreferenceRow {
  type: string;
  name: string;
  description: string;
  email: boolean;
  inApp: boolean;
}

const adminNotificationInfo: Record<string, { name: string; description: string }> = {
  admin_new_booking: {
    name: 'New Booking Requests',
    description: 'When artists submit new booking requests',
  },
  admin_booking_ready: {
    name: 'Booking Ready for Review',
    description: 'When artists mark their bookings as ready',
  },
  admin_booking_rescheduled: {
    name: 'Booking Rescheduled',
    description: 'When bookings are rescheduled by artists',
  },
  admin_booking_cancelled: {
    name: 'Booking Cancelled',
    description: 'When bookings are cancelled',
  },
};

export function AdminNotificationSettings() {
  const { toast } = useToast();
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  
  const [localPreferences, setLocalPreferences] = useState<AdminPreferenceRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Filter only admin notification types
  const adminTypes = Object.keys(adminNotificationInfo);

  useEffect(() => {
    if (preferences) {
      const adminPrefs = preferences
        .filter(pref => adminTypes.includes(pref.notificationType))
        .map(pref => ({
          type: pref.notificationType,
          name: adminNotificationInfo[pref.notificationType]?.name || pref.notificationType,
          description: adminNotificationInfo[pref.notificationType]?.description || '',
          email: pref.email,
          inApp: pref.inApp,
        }));
      
      // If no admin preferences exist yet, don't show the component
      if (adminPrefs.length === 0) {
        setLocalPreferences([]);
        return;
      }
      
      setLocalPreferences(adminPrefs);
    }
  }, [preferences]);

  const handleToggle = (type: string, channel: 'email' | 'inApp') => {
    setLocalPreferences(prev => 
      prev.map(pref => 
        pref.type === type 
          ? { ...pref, [channel]: !pref[channel] }
          : pref
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Get all non-admin preferences to preserve them
    const nonAdminPreferences = preferences?.filter(
      pref => !adminTypes.includes(pref.notificationType)
    ) || [];

    // Combine with updated admin preferences
    const allPreferences = [
      ...nonAdminPreferences,
      ...localPreferences.map(pref => ({
        notificationType: pref.type,
        email: pref.email,
        sms: false, // Admin notifications don't use SMS
        inApp: pref.inApp,
        push: false, // Admin notifications don't use push currently
      }))
    ];

    try {
      await updatePreferences.mutateAsync(allPreferences);
      setHasChanges(false);
      toast({
        title: 'Preferences saved',
        description: 'Your admin notification preferences have been updated.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save preferences. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  // Don't show anything if not an admin (no admin preferences)
  if (localPreferences.length === 0) {
    return null;
  }

  return (
    <Card className="border-white/10 mt-6">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-white/70" />
          <CardTitle>Admin Notifications</CardTitle>
        </div>
        <CardDescription>
          Manage admin notifications. These are disabled by default.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-4 text-muted-foreground">Notification Type</th>
                  <th className="text-center px-4 py-2 min-w-[80px] text-muted-foreground">Email</th>
                  <th className="text-center px-4 py-2 min-w-[80px] text-muted-foreground">In-App</th>
                </tr>
              </thead>
              <tbody>
                {localPreferences.map((pref) => (
                  <tr key={pref.type} className="border-b border-white/10">
                    <td className="py-4 pr-4">
                      <div>
                        <p className="font-medium text-foreground">{pref.name}</p>
                        <p className="text-sm text-muted-foreground">{pref.description}</p>
                      </div>
                    </td>
                    <td className="text-center px-4 py-4">
                      <Switch
                        checked={pref.email}
                        onCheckedChange={() => handleToggle(pref.type, 'email')}
                      />
                    </td>
                    <td className="text-center px-4 py-4">
                      <Switch
                        checked={pref.inApp}
                        onCheckedChange={() => handleToggle(pref.type, 'inApp')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {hasChanges && (
            <div className="flex justify-end pt-4 border-t border-white/10">
              <Button 
                onClick={handleSave}
                disabled={updatePreferences.isPending}
                className="mt-4"
              >
                {updatePreferences.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Preferences
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}